// AUTO-GENERATED FILE - DO NOT EDIT
// This file was automatically generated by the XDK build tool.
// Any manual changes will be overwritten on the next generation.

/**
 * news client for the X API.
 *
 * This module provides a client for interacting with the news endpoints of the X API.
 */

import { Client, ApiResponse, RequestOptions } from '../client.js';
import { 
    Paginator, 
    PostPaginator, 
    UserPaginator, 
    EventPaginator
} from '../paginator.js';
import {
GetResponse,
SearchResponse,
} from './models.js';


/**
 * Options for get method
 * 
 * @public
 */
export interface GetOptions {
    
    
    /** A comma separated list of News fields to display. 
     * Also accepts: news.fields or proper camelCase (e.g., newsFields) */
    newsFields?: Array<any>;
    
    
    
    /** Additional request options */
    requestOptions?: RequestOptions;
    /** Allow original API parameter names (e.g., 'tweet.fields', 'user.fields') and proper camelCase (e.g., 'tweetFields', 'userFields') */
    [key: string]: any;
}


/**
 * Options for search method
 * 
 * @public
 */
export interface SearchOptions {
    
    
    /** The number of results to return. 
     * Also accepts: max_results or proper camelCase (e.g., maxResults) */
    maxResults?: number;
    
    
    
    /** The maximum age of the News story to search for. 
     * Also accepts: max_age_hours or proper camelCase (e.g., maxAgeHours) */
    maxAgeHours?: number;
    
    
    
    /** A comma separated list of News fields to display. 
     * Also accepts: news.fields or proper camelCase (e.g., newsFields) */
    newsFields?: Array<any>;
    
    
    
    /** Additional request options */
    requestOptions?: RequestOptions;
    /** Allow original API parameter names (e.g., 'tweet.fields', 'user.fields') and proper camelCase (e.g., 'tweetFields', 'userFields') */
    [key: string]: any;
}



/**
 * Client for news operations
 * 
 * This client provides methods for interacting with the news endpoints
 * of the X API. It handles authentication, request formatting, and response
 * parsing for all news related operations.
 * 
 * @category news
 */
export class NewsClient {
    private client: Client;

    /**
     * Creates a new news client instance
     * 
     * @param client - The main X API client instance
     */
    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Normalize options object to handle both camelCase and original API parameter names
     * Only accepts: proper camelCase (tweetFields) and original API format (tweet.fields)
     */
    private _normalizeOptions<T extends Record<string, any>>(options: T, paramMappings: Record<string, string>): T {
        if (!options || typeof options !== 'object') {
            return options;
        }
        
        const normalized: any = { ...options };
        
        // For each parameter mapping (original -> proper camelCase)
        for (const [originalName, camelName] of Object.entries(paramMappings)) {
            // Check if original format is used (e.g., 'tweet.fields', 'tweet_fields')
            if (originalName in normalized && !(camelName in normalized)) {
                normalized[camelName] = normalized[originalName];
                delete normalized[originalName];
            }
            // Also check for proper camelCase (e.g., 'tweetFields')
            // If it's already in proper camelCase, keep it (no conversion needed)
            // The camelName is already the proper camelCase format
        }
        
        return normalized as T;
    }



  /**
   * Get news stories by ID
   * Retrieves news story by its ID.


   * @param id The ID of the news story.




   * @returns {Promise<GetResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async get(
        
        
        
        id: string,
        
        
        
        
        
        
        
        
        options: GetOptions = {}
        
    ): Promise<GetResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        
        const paramMappings: Record<string, string> = {
            
            
            'news.fields': 'newsFields',
            
            
        };
        const normalizedOptions = this._normalizeOptions(options || {}, paramMappings);
        
        
        // Destructure options (exclude path parameters, they're already function params)
        const {
            
            
            newsFields = [],
            
            
            
            requestOptions: requestOptions = {}
        } = normalizedOptions;
        

        // Build the path with path parameters
        let path = '/2/news/{id}';
        
        
        path = path.replace('{id}', encodeURIComponent(String(id)));
        
        

        // Build query parameters
        const params = new URLSearchParams();
        
        
        
        
        if (newsFields !== undefined && newsFields.length > 0) {
            
            params.append('news.fields', newsFields.join(','));
            
        }
        
        
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                },
                
                {
                    
                    'OAuth2UserToken': ['tweet.read', 'users.read'],
                    
                },
                
                {
                    
                    'UserToken': [],
                    
                }
                
            ],
            
            
            ...requestOptions
            
        };

        return this.client.request<GetResponse>(
            'GET',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Search News
   * Retrieves a list of News stories matching the specified search query.



   * @param query The search query.



   * @returns {Promise<SearchResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async search(
        
        
        
        
        
        query: string,
        
        
        
        
        
        
        options: SearchOptions = {}
        
    ): Promise<SearchResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        
        const paramMappings: Record<string, string> = {
            
            
            'max_results': 'maxResults',
            
            
            
            'max_age_hours': 'maxAgeHours',
            
            
            
            'news.fields': 'newsFields',
            
            
        };
        const normalizedOptions = this._normalizeOptions(options || {}, paramMappings);
        
        
        // Destructure options (exclude path parameters, they're already function params)
        const {
            
            
            maxResults = undefined,
            
            
            
            maxAgeHours = undefined,
            
            
            
            newsFields = [],
            
            
            
            requestOptions: requestOptions = {}
        } = normalizedOptions;
        

        // Build the path with path parameters
        let path = '/2/news/search';
        

        // Build query parameters
        const params = new URLSearchParams();
        
        
        
        
        if (query !== undefined) {
            
            params.append('query', String(query));
            
        }
        
        
        
        
        
        
        if (maxResults !== undefined) {
            
            params.append('max_results', String(maxResults));
            
        }
        
        
        
        
        
        
        if (maxAgeHours !== undefined) {
            
            params.append('max_age_hours', String(maxAgeHours));
            
        }
        
        
        
        
        
        
        if (newsFields !== undefined && newsFields.length > 0) {
            
            params.append('news.fields', newsFields.join(','));
            
        }
        
        
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                },
                
                {
                    
                    'OAuth2UserToken': ['tweet.read', 'users.read'],
                    
                }
                
            ],
            
            
            ...requestOptions
            
        };

        return this.client.request<SearchResponse>(
            'GET',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }









}
